import React, { useEffect, useRef, useState } from 'react';
import { X, Phone } from 'lucide-react';

const WA_NUMBER  = '917700018187';
const CALL_TEL   = 'tel:+917700018187';
const PHONE_DISP = '+91 7700018187';
const WA_URL     = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi, I'm interested in WASLX. Can you help me?")}`;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.004 2C6.477 2 2.004 6.473 2.004 12c0 1.99.584 3.842 1.586 5.4L2 22l4.737-1.555A9.952 9.952 0 0012.004 22C17.531 22 22 17.527 22 12S17.531 2 12.004 2zm0 18.18a8.157 8.157 0 01-4.394-1.283l-.315-.187-3.26 1.07 1.094-3.168-.206-.325A8.147 8.147 0 013.82 12c0-4.51 3.672-8.18 8.184-8.18S20.18 7.49 20.18 12c0 4.511-3.666 8.18-8.176 8.18z" />
  </svg>
);

const WhatsAppWidget = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Quick contact"
          className="fixed bottom-24 right-5 z-[9999] w-[288px] animate-fade-in rounded-sm bg-surface-raised shadow-overlay"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3.5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-pill bg-whatsapp text-on-whatsapp">
                <WhatsAppIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-bold leading-tight text-content">Quick contact</p>
                <p className="text-sm text-content-muted">Get instant help</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="focus-ring rounded-sm p-1 text-content-muted transition-colors hover:text-content"
              aria-label="Close quick contact"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3 p-4">
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--block bg-whatsapp text-on-whatsapp hover:bg-whatsapp-hover"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Chat on WhatsApp
            </a>

            <a href={CALL_TEL} className="btn-pill btn-pill--primary btn-pill--block">
              <Phone className="h-4 w-4" />
              Call now
            </a>

            <p className="pt-1 text-center text-sm text-content-muted">
              Or call us directly
              <br />
              <span className="font-mono font-bold tabular-nums text-content">{PHONE_DISP}</span>
            </p>
          </div>
        </div>
      )}

      <div className="fixed bottom-5 right-5 z-[9999]">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close quick contact' : 'Open quick contact'}
          aria-expanded={open}
          className="focus-ring relative flex h-14 w-14 items-center justify-center rounded-pill bg-whatsapp text-on-whatsapp shadow-lift transition-colors hover:bg-whatsapp-hover"
        >
          {open ? <X className="h-6 w-6" /> : <WhatsAppIcon className="h-7 w-7" />}
        </button>
      </div>
    </>
  );
};

export default WhatsAppWidget;
